/**
 * Textarea Component - Enhanced multi-line input
 */

import React, { forwardRef, useRef, useEffect } from 'react';
import { colors, spacing, borderRadius } from '../../styles/design-tokens';

const Textarea = forwardRef(({
  label,
  error,
  helper,
  className = '',
  variant = 'flat',
  size = 'md',
  required = false,
  fullWidth = true,
  autoResize = false,
  rows = 3,
  maxRows = 10,
  ...props
}, ref) => {
  const textareaRef = useRef(null);

  // Auto-resize
  useEffect(() => {
    if (!autoResize) return;
    const textarea = textareaRef.current;
    if (!textarea) return;

    const resize = () => {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, maxRows * 24);
      textarea.style.height = `${newHeight}px`;
    };

    textarea.addEventListener('input', resize);
    resize();

    return () => textarea.removeEventListener('input', resize);
  }, [autoResize, maxRows]);

  const variants = {
    flat: 'bg-transparent border-b-2 border-neutral-200 focus:border-brand px-0 py-3',
    box: 'bg-white border-2 border-neutral-200 focus:border-brand px-4 py-3 rounded-lg',
    outline: 'bg-transparent border-2 border-neutral-200 focus:border-brand px-4 py-3 rounded-lg',
    ghost: 'bg-neutral-50 border-2 border-transparent focus:border-brand px-4 py-3 rounded-lg',
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg',
  };

  const hasError = !!error;

  const textareaStyles = `
    w-full
    font-medium
    text-ink
    placeholder:text-neutral-400
    resize-none
    transition-all duration-200
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
    ${hasError ? 'border-error focus:border-error' : ''}
    ${variants[variant] || variants.flat}
    ${sizes[size] || sizes.md}
    ${className}
  `;

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      
      <textarea
        ref={(el) => {
          textareaRef.current = el;
          if (typeof ref === 'function') ref(el);
          else if (ref) ref.current = el;
        }}
        className={textareaStyles}
        rows={rows}
        aria-invalid={hasError}
        aria-describedby={helper ? 'helper-text' : undefined}
        {...props}
      />
      
      {helper && !hasError && (
        <p id="helper-text" className="mt-1 text-xs text-muted">{helper}</p>
      )}
      
      {hasError && (
        <p className="mt-1 text-xs text-error">{error}</p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Textarea; 